/* The one analytics value-type vocabulary, shared by every edge that writes or renders a type: the
   columns editor and reconciler in cirra, the stat tile's value, and the analytics_panel_update tool
   in the api. It lives here because a writer with a narrower private list silently destroys the
   types it does not know — that is how html columns broke once, and how the tool (which only ever
   sends "html") would erase the date/number/list types the reconciler infers. Add a type here and
   every edge keeps it.

   Naming rule so keys stay coherent across panel types: wherever a displayed thing is an entry
   object the field is `type` (table columns `{ key, type }`, chart series `{ key, show, type }`);
   wherever the pointer is a flat `<x>Key` the type is its sibling `<x>Type` (the stat tile's
   `valueKey` / `valueType`). Every one of those fields carries a value from THIS list and is read
   through normalizeAnalyticsColumnType.

   Labels ship with the values so a type can never exist without a human name for it. */
export const ANALYTICS_COLUMN_TYPE_OPTIONS = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'percent', label: 'Percent' },
  { value: 'currency', label: 'Currency' },
  { value: 'duration', label: 'Duration' },
  { value: 'rank', label: 'Rank' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'object', label: 'Object' },
  { value: 'list', label: 'List' },
  { value: 'html', label: 'HTML' },
  /* Presentational: the cell IS a per-row button (its `label` is the button text, its click is the
     panel event `button:<key>`), never a reading of a value. Kept in the one vocabulary so no writer
     destroys it, and excluded from the VALUE subset below so it can never be offered as what a stat
     tile, tooltip field, or trend value "is". */
  { value: 'button', label: 'Button' }
];

export const ANALYTICS_COLUMN_TYPES = ANALYTICS_COLUMN_TYPE_OPTIONS.map((option) => option.value);

/* The types a VALUE can be — everything a formatter could be handed. `button` is a column whose cell
   is a control, so every surface that types a value (stat valueType, tooltip fields, trend, format
   overrides) draws from this subset instead of re-filtering the full list locally. */
export const ANALYTICS_VALUE_COLUMN_TYPE_OPTIONS = ANALYTICS_COLUMN_TYPE_OPTIONS.filter((option) => option.value !== 'button');

export const ANALYTICS_VALUE_COLUMN_TYPES = ANALYTICS_VALUE_COLUMN_TYPE_OPTIONS.map((option) => option.value);

/* Names other systems use for the same type — app response schemas and DuckDB both speak these. */
const TYPE_ALIASES = {
  integer: 'number',
  float: 'number',
  decimal: 'number',
  double: 'number',
  bigint: 'number',
  datetime: 'date',
  timestamp: 'date',
  array: 'list',
  bool: 'boolean',
  text: 'string',
  varchar: 'string',
  /* Presentation synonyms the model and app schemas both reach for. */
  pct: 'percent',
  percentage: 'percent',
  money: 'currency',
  position: 'rank',
  seconds: 'duration'
};

export const normalizeAnalyticsColumnType = (type) => {
  const normalized = String(type || '')
    .trim()
    .toLowerCase();

  return TYPE_ALIASES[normalized] || normalized;
};

/* WHAT AN ABSENT VALUE MEANS, declared per type instead of guessed at the render site.

   An empty result is not an error — a window with no rows makes SUM and AVG return null — but the
   honest rendering of that null depends on the measure:

     count/sum        → 0. "Total clicks: 0" is true.
     percent/rank/avg → no value. 0 would LIE: "average position 0" reads better than rank 1, and
                        "CTR 0%" claims impressions that were never served.

   `number` and `currency` default to zero because a KPI reading a plain number is overwhelmingly a
   count or a sum; anything whose null is genuinely undefined declares its own type (percent, rank)
   or overrides with `emptyAs`. Nothing here depends on the transform author remembering to guard:
   the display layer decides, so a panel cannot render "null%" however sloppy the code upstream. */
const TYPE_EMPTY_BEHAVIOUR = {
  number: 'zero',
  currency: 'zero',
  percent: 'none',
  rank: 'none',
  duration: 'none',
  boolean: 'none',
  date: 'none',
  string: 'none',
  object: 'none',
  list: 'none',
  html: 'none',
  button: 'none'
};

export const EMPTY_VALUE_TEXT = '—';

export const resolveAnalyticsEmptyBehaviour = ({ type, emptyAs = '' } = {}) => {
  const override = String(emptyAs || '')
    .trim()
    .toLowerCase();

  if (override === 'zero' || override === 'none') {
    return override;
  }

  return TYPE_EMPTY_BEHAVIOUR[normalizeAnalyticsColumnType(type)] || 'none';
};

/* Is this value actually a finite analytics number? The formatter and the template mock generator
   share this boundary so numeric text from a source has one meaning on both surfaces. It also keeps
   a badly-written transform from putting garbage such as "null%" on a numeric panel. */
export const toFiniteAnalyticsNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }

  return null;
};

export const ANALYTICS_NUMERIC_COLUMN_TYPES = ['number', 'percent', 'currency', 'duration', 'rank'];
const NUMERIC_TYPES = ANALYTICS_NUMERIC_COLUMN_TYPES;

/* `compact` is what an axis tick needs and a cell never wants: $20k across five ticks against
   $20,000.00 in a table. It is an option rather than a second formatter so a compact currency is
   still THE currency rendering, with the symbol and the grouping decided in one place. */
const groupDigits = (value, maximumFractionDigits, compact = false) => new Intl.NumberFormat('en-US', { maximumFractionDigits, ...(compact ? { notation: 'compact' } : {}) }).format(value);

/* How many items of a list are printed before the rest are counted. A list column exists to say WHO
   or WHAT is behind a number — the five names under a bar, not all 88,403 of them — so the reader
   gets a sample and the size of what they are not seeing. Capped here rather than at each render
   site so a table cell and a chart's hover card cut the same list at the same place.

   The cap is a display bound, not a data bound: the SQL that builds the column should still cut it
   (`list(owner_name)[1:5]`), because a row carrying thousands of names pays for them in every
   payload whether or not anything prints them. */
const LIST_PREVIEW_LIMIT = 5;

/* DATES. A calendar day and an instant are different facts, and the value itself says which: an
   ISO date-only string ("2026-08-19") is a day, anything carrying a time is a moment. The two are
   parsed differently on purpose — a date-only string parsed as a UTC instant displays as the
   PREVIOUS day for every reader west of Greenwich, which is the bug this branch exists to avoid, so
   its parts are read straight off the string and rebuilt at local midnight. The same reasoning and
   the same regexes already sit in the chart axis's label formatter (toChartLabelDisplay), which
   guesses at UNTYPED x values; this branch serves a column that DECLARED itself a date.

   A number is only read as a date at millisecond scale. Anything smaller is unknowable — seconds
   since the epoch, a year, a day count — and inventing a reading for it would print a confident
   wrong date, so it falls through to the digit grouping below. */
const ISO_DATE_ONLY_RE = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
const EPOCH_MILLISECONDS_FLOOR = 100000000000;

const toAnalyticsDateReading = (value) => {
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? { date: value, hasTime: true } : null;
  }

  if (typeof value === 'number') {
    const fromEpoch = value >= EPOCH_MILLISECONDS_FLOOR ? new Date(value) : null;

    return fromEpoch && Number.isFinite(fromEpoch.getTime()) ? { date: fromEpoch, hasTime: true } : null;
  }

  const text = String(value ?? '').trim();
  const dateOnly = text.match(ISO_DATE_ONLY_RE);

  if (dateOnly) {
    const year = Number(dateOnly[1]);
    const month = Number(dateOnly[2]);
    const day = Number(dateOnly[3]);
    const local = new Date(year, month - 1, day);
    /* Round-trip check: "2026-02-31" parses without complaint and rolls into March. */
    const isReal = local.getFullYear() === year && local.getMonth() === month - 1 && local.getDate() === day;

    return isReal ? { date: local, hasTime: false } : null;
  }

  const parsed = new Date(text);

  return Number.isFinite(parsed.getTime()) ? { date: parsed, hasTime: /T\d{2}:\d{2}/.test(text) } : null;
};

/* `timeZone` places an INSTANT — analytics dates mean the workspace's zone, which is a thing only
   the caller knows (see AnalyticsFilterTimezoneNote). It is deliberately ignored for a date-only
   value: that day was rebuilt from its own digits and has no zone to be moved into. */
const formatDate = ({ value, compact = false, timeZone = '' }) => {
  const reading = toAnalyticsDateReading(value);

  /* Unparseable text prints as it came. "Invalid Date" in a KPI tile is worse than the raw cell. */
  if (!reading) {
    return String(value);
  }

  const options = compact ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric', ...(reading.hasTime ? { hour: 'numeric', minute: '2-digit' } : {}) };

  return new Intl.DateTimeFormat('en-US', { ...options, ...(reading.hasTime && timeZone ? { timeZone } : {}) }).format(reading.date);
};

/* A duration is never negative, but a CHANGE in one is, and the change line formats its amount
   through this same branch ("42s faster than last week" is -54s). Clamping at zero printed that
   move as "0s" — a real movement reported as none (2026-08-20). So the magnitude is formatted and
   the sign is carried back out. */
const formatDuration = (totalSeconds) => {
  const sign = totalSeconds < 0 ? '-' : '';
  const seconds = Math.abs(Math.round(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${sign}${hours}h ${String(minutes).padStart(2, '0')}m`;
  }

  if (minutes > 0) {
    return `${sign}${minutes}m ${String(seconds % 60).padStart(2, '0')}s`;
  }

  return `${sign}${seconds}s`;
};

/* THE formatter. One implementation for every surface that shows a typed value — stat tiles, table
   cells, chart axes and hover cards — so the same number reads identically wherever it appears, and
   an absent value follows its type's declared meaning rather than each renderer's local guess.
   Returns display text, always a string.

   Two options exist for AXES rather than for cells, because a strip of ticks is read as one thing:

   - `compact`       $20k over $20,000, so five ticks fit beside a plot. On a date it drops the year
                     and the time, leaving "Aug 19".
   - `fractionDigits` decides the decimals ONCE for every tick. Without it each value decides for
                      itself, which is right for a cell and wrong for an axis — the same axis would
                      print $0, $2, $4.50.

   Neither changes what a value IS, only how much room it is given.

   A third places an instant: `timeZone` is the zone a datetime is printed in, which only the caller
   knows (an analytics page's dates mean the WORKSPACE zone, not the reader's device). Left unset,
   an instant prints in the runtime's own zone. It never applies to a date-only value. */
export const formatAnalyticsTypedValue = ({ value, type = '', emptyAs = '', currency = 'USD', compact = false, fractionDigits = null, timeZone = '' } = {}) => {
  const normalizedType = normalizeAnalyticsColumnType(type);
  /* A list with nothing in it is absent, not a list: an empty aggregate is the same fact as a null
     one, and printing "" would read as a value that happens to be blank. */
  const isAbsent = value === null || value === undefined || value === '' || (Array.isArray(value) && value.length < 1);
  const numeric = NUMERIC_TYPES.includes(normalizedType) ? toFiniteAnalyticsNumber(value) : null;
  /* A numeric type holding something non-numeric is treated as absent: that is the "null%" guard. */
  const isEmpty = isAbsent || (NUMERIC_TYPES.includes(normalizedType) && numeric === null);

  /* A caller-decided digit count wins over the per-value one wherever a branch below has an opinion,
     which is what makes a tick strip agree with itself. */
  const digits = (perValue) => (Number.isFinite(fractionDigits) ? fractionDigits : perValue);

  if (isEmpty) {
    return resolveAnalyticsEmptyBehaviour({ type: normalizedType, emptyAs }) === 'zero' ? formatAnalyticsTypedValue({ value: 0, type: normalizedType, currency, compact, fractionDigits }) : EMPTY_VALUE_TEXT;
  }

  if (normalizedType === 'percent') {
    return `${groupDigits(numeric, digits(2), compact)}%`;
  }

  if (normalizedType === 'currency') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: digits(Number.isInteger(numeric) ? 0 : 2), ...(compact ? { notation: 'compact' } : {}) }).format(numeric);
  }

  if (normalizedType === 'duration') {
    return formatDuration(numeric);
  }

  if (normalizedType === 'rank') {
    return groupDigits(numeric, digits(1), compact);
  }

  if (normalizedType === 'number') {
    return groupDigits(numeric, digits(2), compact);
  }

  if (normalizedType === 'boolean') {
    return value === true ? 'Yes' : 'No';
  }

  /* A day, or a moment — the value decides which (see formatDate). Compact drops the year and the
     time, for a tick strip that has room for "Aug 19" and nothing more. */
  if (normalizedType === 'date') {
    return formatDate({ value, compact, timeZone });
  }

  /* A list prints a sample of its items and how many it is not printing. Chosen by the value being
     an array as well as by the declared type, because no other branch here can render one and the
     untyped passthrough below would print plausible-but-wrong text for it ("a,b", unspaced and
     uncapped). Items are formatted untyped: an array carries one type for itself, not one per item,
     so a number inside one still gets its digit grouping and nothing else is claimed about it. */
  if (normalizedType === 'list' || Array.isArray(value)) {
    const items = Array.isArray(value) ? value : [value];
    const shown = items.slice(0, LIST_PREVIEW_LIMIT).map((item) => formatAnalyticsTypedValue({ value: item, currency }));
    const remaining = items.length - shown.length;

    return remaining > 0 ? `${shown.join(', ')} +${groupDigits(remaining, 0)} more` : shown.join(', ');
  }

  /* Untyped and text-ish values pass through, and a bare number still gets digit grouping so an
     un-declared count does not regress to 81453. */
  const inferred = toFiniteAnalyticsNumber(value);

  return inferred !== null && typeof value !== 'boolean' ? groupDigits(inferred, digits(2), compact) : String(value);
};
